import { CalendarContext, SelectDateRange } from "Components/Calendar/context"
import { useAssign, useInputChange } from "Hooks/basic"
import { useAddHealthBoard, useUpdateHealthBoard } from "Hooks/board"
import { timeFormatStr } from "Methods/basic"
import { InitialModal } from "Methods/modal"
import { checkUserPrivilege } from "Methods/privilege"
import request from "Methods/request"
import { Board, BoardModifyDialog } from "Types/board"
import { AppContext } from "Views/Home/context"
import moment from "moment"
import { useContext, useEffect, useMemo } from "react"

// 多条预约时的时间范围生成工具
export function useCreateModalTimeStr(selectDateRange: SelectDateRange | null) {
    return useMemo(() => {
        let modalTimeStr = ""
        const adTimes: number[] = []
        if (!selectDateRange) {
            return {
                adTimes: [],
                modalTimeStr: "",
            }
        }
        const dateRange = selectDateRange as SelectDateRange
        const startTime = Math.min(dateRange.start.getTime(), dateRange.end.getTime())
        const endTime = Math.max(dateRange.start.getTime(), dateRange.end.getTime())
        const startDate = new Date(startTime)
        const endDate = new Date(endTime)
        const startDay = startDate.getDay() || 7
        const endDay = endDate.getDay() || 7
        if (startTime === endTime) {
            modalTimeStr = `${moment(startDate).format(timeFormatStr.month)}`
        } else {
            modalTimeStr = `${moment(startDate).format(timeFormatStr.month)} - ${moment(endDate).format(timeFormatStr.month)}`
        }
        for (let i = 0; i <= endDay - startDay; i++) {
            adTimes.push(startTime + (i * 1000 * 3600 * 24))
        }
        return { modalTimeStr, adTimes }
    }, [selectDateRange])
}

type CreateType = "single" | "multiple"
export function useCreate(type: CreateType) {
    const initialExternal = {
        articleTypeId: 0,
    }
    const {
        object: external,
        handleChange: setExternals,
    } = useAssign(initialExternal)
    const { roleMap } = useContext(AppContext)
    const { isBusiness } = roleMap
    const {
        boardModifyDialog, handleChangeBoardModifyDialog,
        selectPlaceId, selectDateRange, resetPlaceId,
    } = useContext(CalendarContext)
    const dialogData = boardModifyDialog as BoardModifyDialog

    const value = useCreateModalTimeStr(selectDateRange)
    const adTime = dialogData.date.getTime()
    const modalTimeStr = moment(dialogData.date).format(timeFormatStr.all)

    const { text, handleChange, clearText } = useInputChange("")
    const { text: projectId, handleChange: handleProjectIdChange, clearText: clearProjectId } = useInputChange("")
    const { text: projectMoney, handleChange: handleProjectMoneyChange, clearText: clearProjectMoney } = useInputChange("")
    const { add } = useAddHealthBoard()
    const handleChangeArticleType = (value: number) => setExternals({ articleTypeId: Number(value) })

    const callback = async () => {
        await add({
            name: text,
            ad_time: adTime,
            seq_id: dialogData.place.seq_num,
            ad_place_id: dialogData.place._id,
            health_article_type_id: external.articleTypeId,
            project_id: projectId || '',
            project_money: projectMoney || '',
        })
    }
    const cancelCallback = () => {
        handleChangeBoardModifyDialog(null)
        clearText()
        clearProjectId()
        clearProjectMoney()
        resetPlaceId()
    }
    const disabled = text === "" || !external.articleTypeId || (isBusiness && (projectId.length > 50 || projectMoney.length > 50 || projectId === ""))
    return {
        callback, cancelCallback,
        dialogData, disabled, modalTimeStr,
        text, handleChange,
        projectId, handleProjectIdChange,
        projectMoney, handleProjectMoneyChange,
        handleChangeArticleType
    }
}
// 修改点位
export function useUpdate() {
    const {
        boardModifyDialog, handleChangeBoardModifyDialog, handleSetEditMaterial
    } = useContext(CalendarContext)
    const dialogData = boardModifyDialog as BoardModifyDialog
    const board = dialogData.board as Board
    const isPass = dialogData.isPass
    const {  handleChangeModal, privilegeMap, roleMap, user } = useContext(AppContext)
    const { update: materialUpdate = false } = checkUserPrivilege(privilegeMap, "material")
    const { isUser, isBusiness } = roleMap
    const { text, handleChange, clearText, setText } = useInputChange("")
    const { text: projectId, handleChange: handleProjectIdChange, clearText: clearProjectId, setText: setProjectId } = useInputChange("")
    const { text: projectMoney, handleChange: handleProjectMoneyChange, clearText: clearProjectMoney, setText: setProjectMoney } = useInputChange("")
    const initialExternal = {
        articleTypeId: 0,
    }
    const {
        object: external,
        handleChange: setExternals,
    } = useAssign(initialExternal)
    useEffect(() => {
        const articleTypeId = board.article_type === null || board.article_type.hidden ? 0 : board.health_article_type_id
        setExternals({
            articleTypeId,
        })
        setText(board.name)
        setProjectId(board.project_id)
        setProjectMoney(board.project_money)
    }, [board, setText, setProjectId, setProjectMoney])
    const { update } = useUpdateHealthBoard()
    const callback = async () => {
        await update({
            variables: {
                id: board.id,
                name: text,
                health_article_type_id: external.articleTypeId,
                project_id: projectId || '',
                project_money: projectMoney || '',
            },
        })
    }
    const cancelCallback = () => {
        handleChangeBoardModifyDialog(null)
        clearText()
        clearProjectId()
        clearProjectMoney()
    }
    const editCallback = async () => {
        const { data } = await request.get(`/apis/material/${board.material.id}`)
        handleSetEditMaterial({
            open: true,
            update: materialUpdate,
            isBom: isUser,
            modifyData: data,
            handleClose: () => {
                handleSetEditMaterial({
                    modifyData: null,
                    open: false,
                    update: false,
                    isBom: false,
                    handleClose: () => { },
                    handleConfirm: () => { },
                })
            },
            handleConfirm: async (newItem) => {
                try {
                    await request.put(`/apis/material/${newItem.id}/update`, newItem)
                    handleChangeModal({
                        show: true,
                        type: "success",
                        title: "修改成功！",
                        timeout: 800,
                        onConfirm: () => {
                            handleChangeModal(InitialModal)
                        },
                    })
                } catch (err) {
                    if (!navigator.onLine) {
                        handleChangeModal({
                            show: true,
                            type: "warning",
                            customButtons: [],
                            title: "oops! 断网啦！",
                            timeout: 3000,
                            onConfirm: () => {
                                handleChangeModal(InitialModal)
                            },
                        })
                        return
                    }
                    const { response: { data = {} } = {}, message } = err
                    handleChangeModal({
                        show: true,
                        type: "warning",
                        customButtons: [],
                        title: data.message || message,
                        timeout: 3000,
                        onConfirm: () => {
                            handleChangeModal(InitialModal)
                        },
                    })
                }
                handleSetEditMaterial({
                    modifyData: null,
                    open: false,
                    update: false,
                    isBom: false,
                    handleClose: () => { },
                    handleConfirm: () => { },
                })
            },
        })
    }
    const handleChangeArticleType = (value: number) => setExternals({ articleTypeId: Number(value) })
    const dateStr = moment(dialogData.date).format(timeFormatStr.all)
    const disabled = text === "" || !external.articleTypeId || (isBusiness && (projectId.length > 50 || projectMoney.length > 50 || projectId === ""))
    return {
        callback, cancelCallback,
        dialogData, disabled, dateStr, isPass,
        text, handleChange,
        projectId, handleProjectIdChange,
        projectMoney, handleProjectMoneyChange,
        handleChangeArticleType, external,
        editCallback,
    }
}

export function useCreateHealthDraft() {
    const initialExternal = {
        articleTypeId: 0,
    }
    const {
        object: external,
        handleChange: setExternals,
    } = useAssign(initialExternal)
    const {
        boardModifyDialog, handleChangeBoardModifyDialog,
        selectPlaceId, resetPlaceId,
    } = useContext(CalendarContext)
    const dialogData = boardModifyDialog as BoardModifyDialog

    const modalTimeStr = moment(dialogData.date).format(timeFormatStr.all)

    const { text, handleChange, clearText } = useInputChange("")
    const { add } = useAddHealthBoard()
    const handleChangeArticleType = (value: number) => setExternals({ articleTypeId: Number(value) })

    const callback = async () => {
        await add({
            name: text,
            ad_time: new Date().getTime(),
            seq_id: 1,
            ad_place_id: Number(process.env.HEALTH_PLACE || 285),
            health_article_type_id: external.articleTypeId,
            is_draft: true,
        })
    }
    const cancelCallback = () => {
        handleChangeBoardModifyDialog(null)
        clearText()
        resetPlaceId()
    }
    const disabled = text === "" || !external.articleTypeId
    return {
        callback, cancelCallback,
        dialogData, disabled, modalTimeStr,
        text, handleChange,
        handleChangeArticleType
    }
}
